import {AfterViewInit, Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {Router, NavigationEnd} from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import {RecaptchaComponent} from 'ng-recaptcha';

import data from '../../data';
import { faFacebook, faInstagram, faLinkedin } from '../icons/brands';
import {Tools} from '../tools';
import {MainServices} from '../services/MainServices';
import {apiErrorMessage} from '../services/response.service';
import {environment} from '../../environments/environment';

const weekDayFormater = Intl.DateTimeFormat(!localStorage.getItem('region') ?  'es-mX' : localStorage.getItem('region'), { weekday: 'long', timeZone: 'UTC' });
function getWeekDay(weekday: number) {
  return weekDayFormater.format(new Date(`2017-01-0${weekday} 00:00:00`));
}

const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

@Component({
    selector: 'app-footer',
    templateUrl: './footer.component.html',
    styleUrls: ['./footer.component.scss'],
    standalone: false
})
export class FooterComponent implements OnInit, AfterViewInit {

  @ViewChild('btnEnglish') btnEnglish;
  @ViewChild('btnSpanish') btnSpanish;
  @ViewChild('reCaptcha') reCaptcha: RecaptchaComponent;
  @ViewChild('captchaSlot') captchaSlot: ElementRef;

  // El reCAPTCHA se renderiza de forma diferida (al acercarse el formulario o al enfocarlo)
  public showCaptcha = false;
  private captchaObserver: IntersectionObserver;

  public redesSociales = data.redesSociales;
  public correo = data.contacto.correo;
  public telefono = data.contacto.telefono;
  public currentYear = new Date().getFullYear();
  public icons = {
    facebook: faFacebook,
    instagram: faInstagram,
    linkedin: faLinkedin,
  };
  public horarios = data.horarios.map(({ inicio, fin }) => {
    const dias = getWeekDay(inicio.dia) + (inicio.dia === fin.dia ? '' : ' a ' + getWeekDay(fin.dia));
    const horario = `de ${inicio.hora}:00 a ${fin.hora}:00 hrs`;
    return capitalize(`${dias} ${horario}`);
  });

  // Route-based visibility (arranca oculto; se activa solo en el inicio)
  public showContactForm = false;

  // Contact form
  public submitted = false;
  public successalert = false;
  public form: UntypedFormGroup;
  public captchaPublicKey = environment.captcha_public_key;
  public captchaResponse: string;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private spinnerService: NgxSpinnerService,
    private service: MainServices,
    private router: Router,
  ) {
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.showContactForm = this.esInicio(event.urlAfterRedirects);
      }
    });
  }

  /** El formulario de contacto (y su reCAPTCHA) solo vive en el inicio. */
  private esInicio(url: string): boolean {
    return url === '/' || url === '';
  }

  ngOnInit() {
    // Si el router ya completo su primera navegacion (init bloqueante o nav previa),
    // fija la visibilidad de inmediato para que el formulario NO parpadee en rutas que
    // no son el inicio: eso evitaba un salto de layout (CLS) y la carga del reCAPTCHA
    // (~375KB) en paginas como /bolsa-trabajo.
    if (this.router.navigated) {
      this.showContactForm = this.esInicio(this.router.url);
    }
    this.form = this.formBuilder.group({
      nombre: ['', [Validators.required, Validators.minLength(5), Validators.maxLength(35)]],
      comentario: ['', [Validators.required, Validators.maxLength(2000)]],
      email: ['', [Validators.required, Validators.email]],
      // POST /contactos exige `phone` de 10 dígitos.
      telefono: ['', [Validators.required, Validators.pattern('^[0-9]{10}$')]],
      recaptchaReactive: new UntypedFormControl(null, Validators.required)
    });
  }

  ngAfterViewInit() {
    // Carga diferida del reCAPTCHA: solo cuando el formulario está por entrar en pantalla.
    if (this.showCaptcha || !this.captchaSlot || typeof IntersectionObserver === 'undefined') {
      return;
    }
    this.captchaObserver = new IntersectionObserver((entries) => {
      if (entries.some(entry => entry.isIntersecting)) {
        this.enableCaptcha();
      }
    }, { rootMargin: '200px' });
    this.captchaObserver.observe(this.captchaSlot.nativeElement);
  }

  public enableCaptcha() {
    if (this.showCaptcha) { return; }
    this.showCaptcha = true;
    this.captchaObserver?.disconnect();
  }

  public onCompleteCaptcha(response: string) {
    this.captchaResponse = response;
    this.form.get('recaptchaReactive')?.setValue(response);
  }

  async onSubmit() {
    this.submitted = true;
    const {nombre, comentario, email, telefono} = this.form.value;
    if (this.form.invalid) { return; }
    this.spinnerService.show();
    // POST /contactos responde 201 con data.enviado; un fallo de SMTP devuelve
    // 502 MAIL_FAILED en vez de fingir el envío como hacía el legado.
    this.service.sendContactoInformation(
      nombre,
      email,
      telefono,
      comentario,
      this.captchaResponse
    ).then(data => {
      this.successalert = !!data.enviado;
    }).catch(async e => {
      const Swal = (await import('sweetalert2')).default;
      Swal.fire('Error', apiErrorMessage(e, 'No se pudo enviar tu mensaje. Intenta de nuevo.'), 'error');
    }).finally(() => {
      this.spinnerService.hide();
      this.form.reset();
      this.captchaResponse = null;
      this.reCaptcha?.reset();
      setInterval(() => this.successalert = false, 3500);
      this.submitted = false;
    });
  }

  isInvalid(field: string, validation: string) {
    return false;
  }

  public changeLanguage(lang: string) {
    if (lang === 'en') {
      Tools.setLanguagePage('en-US', 'en');
    } else {
      Tools.setLanguagePage('es-MX', 'es');
    }
  }

  public getCurrentLanguage(): string{
    const lang = localStorage.getItem('language');
    if (!lang || lang === 'es') { return 'es'; }
    else { return lang;}
  }
}
